import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ProjectThumbnail } from "../components/ProjectThumbnail";
import { projects } from "../data/portfolio";

const ProjectRow = ({ project, index }) => {
  const flipped = index % 2 === 1;

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${index % 2 === 1 ? "lg:mt-16" : "lg:mt-0"}`}
      data-testid={`project-card-${project.id}`}
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
        {/* Image */}
        <div
          className={`col-span-12 lg:col-span-8 ${flipped ? "lg:order-2" : ""}`}
        >
          <Link to={`/work/${project.id}`} className="block cursor-pointer" data-testid={`project-thumbnail-link-${project.id}`}>
            <ProjectThumbnail project={project} index={index} />
          </Link>
        </div>

        {/* Text */}
        <div
          className={`col-span-12 lg:col-span-4 ${flipped ? "lg:order-1 lg:text-right" : ""}`}
        >
          <div className="eyebrow mb-4">{project.category}</div>
          <h3
            className="font-display font-light leading-[0.95] tracking-tight text-3xl md:text-4xl lg:text-5xl text-[#0E1A2B] mb-6"
            data-testid={`project-title-${project.id}`}
          >
            {project.title}
          </h3>
          <p className="text-[#6E7583] text-base md:text-lg leading-relaxed max-w-md ml-auto md:ml-0">
            {project.summary}
          </p>
          <div className="mt-6 text-xs tracking-[0.22em] uppercase text-[#6E7583]">
            {project.role}
          </div>

          {(project.id === "uniacco-pdp" || project.id === "uniacco-copilot") &&
          project.study?.impactTop?.length ? (
            <div
              className={`mt-5 flex flex-wrap gap-x-5 gap-y-2 max-w-md ml-auto md:ml-0 ${
                flipped ? "md:justify-end" : ""
              }`}
              data-testid={`project-metrics-${project.id}`}
            >
              {project.study.impactTop.slice(0, 3).map((m, i) => (
                <div key={i} className="flex items-baseline gap-1.5">
                  <span className="font-numeric text-lg md:text-xl text-[#E5326A]">
                    {m.value}
                  </span>
                  <span className="text-[11px] tracking-[0.1em] uppercase text-[#6E7583]">
                    {m.label}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <Link
            to={`/work/${project.id}`}
            className={`group mt-8 inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-[#0E1A2B] ${
              flipped ? "lg:justify-end" : ""
            }`}
            data-testid={`project-view-${project.id}`}
          >
            <span className="link-underline">View case study</span>
            <ArrowUpRight
              size={16}
              className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              style={{ color: project.accent }}
            />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export const WorkSection = () => {
  return (
    <section
      id="work"
      className="section-pad relative overflow-hidden"
      data-testid="work-section"
    >
      

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-16"
        >
          <div>
            <div className="eyebrow mb-6">— Selected work</div>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-5xl md:text-7xl text-[#0E1A2B] max-w-3xl">
              A few of many things I've been <span className="text-[#E5326A]">proud</span> to shape.
            </h2>
          </div>
        </motion.div>

        <div className="flex flex-col gap-20 md:gap-24">
          {projects.map((p, i) => (
            <ProjectRow key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkSection;
